import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from '@tanstack/react-router'
import { hasRoleInDistrict, useCurrentUser, type MembershipRole } from './useCurrentUser'
import { useActiveDistrict } from './useActiveDistrict'

/**
 * Route-level role gate. Renders the page (Outlet, or explicit children) only
 * when the signed-in user has `requiredRole` in the ACTIVE district. Nav
 * items for restricted pages are already hidden (features/_shell/roleVisibility),
 * but URLs remain reachable by typing them — this component blocks that path
 * with a localized "not authorized" panel. Server-side handlers remain the
 * source of truth for writes; this is a defence-in-depth / UX layer.
 */
export function RoleGate({ requiredRole, children }: { requiredRole: MembershipRole; children?: ReactNode }) {
  const { t } = useTranslation('common')
  const { data: user, isPending } = useCurrentUser()
  const { activeDistrictId } = useActiveDistrict()

  if (isPending) {
    return (
      <main className="admin-page">
        <p>{t('states.loading')}</p>
      </main>
    )
  }

  if (!hasRoleInDistrict(user, activeDistrictId, requiredRole)) {
    return (
      <main className="admin-page">
        <h1>{t('states.accessDenied')}</h1>
      </main>
    )
  }

  return <>{children ?? <Outlet />}</>
}
